// C04/C06/C07/C09/C13/C14 — channel & distribution calculators.
import { useMemo, useState } from "react";
import { channelEconomics, distributorRoi, dealerSkuGap, channelConflict, landedCost, sellThrough, inr, inr2, pct } from "./engines";
import { Field, SliderField, ResultRow, Waterfall, ToolSection, Note, useNumbers } from "./ui";

/* ---------- C04 Channel Economics Comparator ---------- */
const C04_DEFAULT = [
  { name: "General trade (distributor)", realizationPct: 70, cogsPct: 52, channelMarginPct: 24, logisticsPct: 3, creditDays: 21, servicePct: 1, monthlyUnits: 8000 },
  { name: "Modern trade", realizationPct: 78, cogsPct: 52, channelMarginPct: 28, logisticsPct: 4, creditDays: 45, servicePct: 4, monthlyUnits: 3000 },
  { name: "Marketplace", realizationPct: 92, cogsPct: 52, channelMarginPct: 30, logisticsPct: 8, creditDays: 10, servicePct: 5, monthlyUnits: 2000 },
  { name: "D2C website", realizationPct: 100, cogsPct: 52, channelMarginPct: 3, logisticsPct: 9, creditDays: 2, servicePct: 7, monthlyUnits: 600 },
];
export function ToolC04() {
  const [channels, setChannels] = useState(C04_DEFAULT);
  const [mrp, setMrp] = useState(100);
  const r = useMemo(() => channelEconomics(channels, { mrp, financeRatePct: 12 }), [channels, mrp]);
  const setC = (i, k) => (val) => setChannels((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: val } : x)));
  return (
    <div data-testid="tool-C04">
      <div className="max-w-xs"><Field label="MRP / list price (₹)" value={mrp} onChange={setMrp} testid="c04-mrp" /></div>
      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="c04-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b border-black">
              <th className="py-2 pr-3 font-medium">Channel</th>
              {["Realisation %", "COGS %", "Channel cost %", "Logistics %", "Credit days", "Service %", "Units /mo"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
              <th className="py-2 font-medium text-right">Contribution / unit</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((c, i) => (
              <tr key={i} className="border-b border-black/10" data-testid={`c04-row-${i}`}>
                <td className="py-2 pr-3"><input value={c.name} onChange={(e) => setC(i, "name")(e.target.value)} className="w-44 bg-transparent outline-none border-b border-transparent focus:border-black text-sm" data-testid={`c04-name-${i}`} /></td>
                {["realizationPct", "cogsPct", "channelMarginPct", "logisticsPct", "creditDays", "servicePct", "monthlyUnits"].map((k) => (
                  <td key={k} className="py-2 pr-3">
                    <input type="number" value={c[k]} onChange={(e) => setC(i, k)(e.target.value)} className="w-16 border border-zinc-200 px-1.5 py-1 text-sm outline-none focus:border-black" data-testid={`c04-${k}-${i}`} />
                  </td>
                ))}
                <td className={`py-2 text-right tabular-nums font-medium ${r.rows.find((x) => x.name === c.name)?.contribution < 0 ? "text-red-700" : ""}`}>
                  {inr2(r.rows.find((x) => x.name === c.name)?.contribution ?? 0)} ({r.rows.find((x) => x.name === c.name)?.contributionPct ?? 0}% of realisation)
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-6" data-testid="c04-result">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Ranked by contribution %</div>
          {r.rows.map((row, i) => (
            <ResultRow key={row.name} label={`#${i + 1} ${row.name}`} value={`${inr2(row.contribution)} · ${row.contributionPct}%`} strong={i === 0} tone={row.contribution < 0 ? "bad" : i === 0 ? "good" : undefined} />
          ))}
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Monthly contribution</div>
          {r.rows.map((row) => (
            <ResultRow key={row.name} label={row.name} value={inr(row.monthlyContribution)} tone={row.monthlyContribution < 0 ? "bad" : undefined} />
          ))}
        </div>
      </div>
      <Note>Same MRP, four P&Ls. Channel cost % includes margins, fees and schemes; service % includes demos, merchandising and claims. Credit cost is computed at 12% p.a.</Note>
    </div>
  );
}

/* ---------- C06 Distributor ROI Simulator ---------- */
export function ToolC06() {
  const [v, set] = useNumbers({ monthlyPurchase: 1000000, marginPct: 8, monthlyExpenses: 42000, capitalDeployed: 0, inventoryDays: 22, marketCreditDays: 32, companyCreditDays: 7 });
  const r = useMemo(() => distributorRoi(v), [v]);
  return (
    <div data-testid="tool-C06" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <Field label="Distributor's monthly purchase from you (₹)" value={v.monthlyPurchase} onChange={set("monthlyPurchase")} testid="c06-purchase" />
        <SliderField label="Gross margin earned" hint="Trade margin + schemes, before their costs." value={v.marginPct} onChange={set("marginPct")} min={0} max={25} step={0.5} testid="c06-margin" />
        <Field label="Their monthly expenses (₹)" hint="Godown, staff, van, fuel, admin." value={v.monthlyExpenses} onChange={set("monthlyExpenses")} testid="c06-expenses" />
        <Field label="Capital they have deployed (₹)" hint="Leave 0 to estimate it from the cash cycle — stock days + market credit − the credit you give." value={v.capitalDeployed} onChange={set("capitalDeployed")} testid="c06-capital" />
        <div className="grid grid-cols-3 gap-4">
          <Field label="Stock days" value={v.inventoryDays} onChange={set("inventoryDays")} testid="c06-stock" />
          <Field label="Market credit days" value={v.marketCreditDays} onChange={set("marketCreditDays")} testid="c06-mcredit" />
          <Field label="Credit you give" value={v.companyCreditDays} onChange={set("companyCreditDays")} testid="c06-ccredit" />
        </div>
      </div>
      <div>
        <div data-testid="c06-result">
          <ResultRow label="Gross margin / month" value={inr(r.grossMarginMonthly)} />
          <ResultRow label="Net earnings / month" value={inr(r.netEarningsMonthly)} strong tone={r.viable ? "good" : "bad"} />
          <ResultRow label="Cash cycle" value={`${r.cycleDays} days`} />
          <ResultRow label="Stock rotations / month" value={String(r.rotationsPerMonth)} />
          <ResultRow label="Return on capital (annualised)" value={`${Math.round(r.annualRoiPct)}%`} strong tone={r.annualRoiPct >= 24 ? "good" : "bad"} />
        </div>
        <Note tone={r.annualRoiPct < 24 ? "warn" : "info"}>
          {r.annualRoiPct < 24
            ? "Below ~24% annualised, a distributor earns less than the cost and risk of capital — expect under-investment in your brand, or attrition."
            : "Healthy partner economics. Protect them: late claims and forced loading erode this fast."}
        </Note>
        <ToolSection title="Levers, ranked by partner impact">
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-600">
            <li>Cut market credit days — the cheapest ROI lever you control jointly.</li>
            <li>Raise rotation (smaller, frequent orders) before raising margin.</li>
            <li>Pay claims in 30 days — unpaid claims are a hidden margin cut.</li>
          </ul>
        </ToolSection>
      </div>
    </div>
  );
}

/* ---------- C07 Dealer–SKU White-Space Finder ---------- */
const C07_DEALERS = [{ name: "Dealer A", monthlyVolume: 500000 }, { name: "Dealer B", monthlyVolume: 300000 }, { name: "Dealer C", monthlyVolume: 160000 }];
const C07_SKUS = [{ name: "Core SKU 1", avgPerDealer: 60000 }, { name: "Core SKU 2", avgPerDealer: 45000 }, { name: "Premium SKU", avgPerDealer: 30000 }, { name: "New launch", avgPerDealer: 20000 }];
export function ToolC07() {
  const [dealers, setDealers] = useState(C07_DEALERS);
  const [skus] = useState(C07_SKUS);
  const [stocked, setStocked] = useState({ 0: [0, 1], 1: [0, 2], 2: [0] });
  const r = useMemo(() => dealerSkuGap({ dealers, skus, stocked }), [dealers, skus, stocked]);
  const toggle = (di, si) => setStocked((s) => {
    const cur = s[di] || [];
    return { ...s, [di]: cur.includes(si) ? cur.filter((x) => x !== si) : [...cur, si] };
  });
  return (
    <div data-testid="tool-C07" className="grid lg:grid-cols-2 gap-10">
      <div>
        <p className="text-sm text-zinc-600 mb-4">Tick what each dealer actually stocks. Unticked cells are your white space, sized by dealer volume.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="c07-matrix">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b border-black">
                <th className="py-2 pr-3 font-medium">Dealer</th>
                {skus.map((s, si) => <th key={si} className="py-2 pr-3 font-medium text-center">{s.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {dealers.map((d, di) => (
                <tr key={di} className="border-b border-black/10" data-testid={`c07-dealer-${di}`}>
                  <td className="py-2 pr-3">
                    <input value={d.name} onChange={(e) => setDealers((arr) => arr.map((x, xi) => (xi === di ? { ...x, name: e.target.value } : x)))} className="w-28 bg-transparent outline-none border-b border-transparent focus:border-black text-sm" />
                    <div className="text-xs text-zinc-400">{inr(d.monthlyVolume)}/mo</div>
                  </td>
                  {skus.map((s, si) => (
                    <td key={si} className="py-2 text-center">
                      <input type="checkbox" checked={(stocked[di] || []).includes(si)} onChange={() => toggle(di, si)} className="w-4 h-4 accent-black" data-testid={`c07-cell-${di}-${si}`} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <div data-testid="c07-result">
          <ResultRow label="Open dealer–SKU gaps" value={String(r.gapCount)} />
          <ResultRow label="Monthly opportunity if filled" value={inr(r.totalOpportunity)} strong />
          <div className="mt-6 text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Priority dealers</div>
          {r.byDealer.map((d) => (
            <ResultRow key={d.name} label={`${d.name} — ${d.missing} missing`} value={inr(d.opportunity)} tone={d.opportunity > 0 ? "bad" : "good"} />
          ))}
          <div className="mt-6 text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Top 5 actions</div>
          <ol className="list-decimal pl-5 space-y-1.5 text-sm text-zinc-700" data-testid="c07-actions">
            {r.gaps.slice(0, 5).map((g, i) => (
              <li key={i}>Place <strong>{g.sku}</strong> at <strong>{g.dealer}</strong> — worth ≈ {inr(g.opportunity)}/mo</li>
            ))}
            {r.gaps.length === 0 && <li>Full assortment everywhere — white space is zero. Now check depth and share of shelf.</li>}
          </ol>
        </div>
      </div>
    </div>
  );
}

/* ---------- C09 Omnichannel Conflict Simulator ---------- */
export function ToolC09() {
  const [v, set] = useNumbers({ mrp: 500, onlineSellingPrice: 380, dealerPrice: 475, dealerMarginPct: 22, distributorMarginPct: 6, onlineSharePct: 25 });
  const r = useMemo(() => channelConflict(v), [v]);
  return (
    <div data-testid="tool-C09" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="MRP (₹)" value={v.mrp} onChange={set("mrp")} testid="c09-mrp" />
          <Field label="Dealer selling price (₹)" value={v.dealerPrice} onChange={set("dealerPrice")} testid="c09-dealer" />
        </div>
        <Field label="Online selling price (₹)" hint="After discounts, before platform subsidies." value={v.onlineSellingPrice} onChange={set("onlineSellingPrice")} testid="c09-online" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Dealer margin %" value={v.dealerMarginPct} onChange={set("dealerMarginPct")} testid="c09-dmargin" />
          <Field label="Distributor margin %" value={v.distributorMarginPct} onChange={set("distributorMarginPct")} testid="c09-dsmargin" />
        </div>
        <SliderField label="Online share of your sales" value={v.onlineSharePct} onChange={set("onlineSharePct")} testid="c09-share" />
      </div>
      <div>
        <div data-testid="c09-result">
          <ResultRow label="Online undercut vs dealer" value={`${r.undercutPct}%`} tone={r.undercutPct > 10 ? "bad" : undefined} />
          <ResultRow label="Dealer margin today" value={`${inr2(r.dealerMarginRs)} / unit`} />
          <ResultRow label="Dealer margin if they price-match" value={`${inr2(r.matchedMarginRs)} / unit`} tone="bad" />
          <ResultRow label="Margin erosion on match" value={`${r.marginErosionPct}%`} tone={r.marginErosionPct > 30 ? "bad" : undefined} />
          <ResultRow label="Conflict index (0–100)" value={String(r.conflictIndex)} strong tone={r.severity === "high" ? "bad" : undefined} />
        </div>
        <Note tone={r.severity === "low" ? "info" : "warn"}>
          {r.severity === "high" && "Severe: dealers are subsidising your online growth. Expect delisting threats and coverage loss within two quarters."}
          {r.severity === "medium" && "Friction is real but manageable — act before the festival season amplifies it."}
          {r.severity === "low" && "Channels are broadly in parity. Keep watching platform-funded discounts — they move weekly."}
        </Note>
        <ToolSection title="Mitigations that actually work">
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-600">
            <li>Differentiate packs: online-exclusive sizes make prices incomparable.</li>
            <li>Set a floor via MAP-style policy and enforce it on your own listings first.</li>
            <li>Compensate dealers for online-driven footfall with display support, not price cuts.</li>
            <li>Reserve deep online discounting for end-of-life SKUs.</li>
          </ul>
        </ToolSection>
      </div>
    </div>
  );
}

/* ---------- C13 Export Landed Economics ---------- */
export function ToolC13() {
  const [v, set] = useNumbers({ exWorks: 100, freight: 14, insurancePct: 1, dutyPct: 30, clearance: 6, localFreight: 8, importerMarginPct: 12, distributorMarginPct: 15, retailerMarginPct: 35 });
  const r = useMemo(() => landedCost(v), [v]);
  return (
    <div data-testid="tool-C13" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Ex-works price (₹)" value={v.exWorks} onChange={set("exWorks")} testid="c13-exw" />
          <Field label="Freight to port (₹/unit)" value={v.freight} onChange={set("freight")} testid="c13-freight" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Insurance %" value={v.insurancePct} onChange={set("insurancePct")} testid="c13-ins" />
          <Field label="Import duty %" value={v.dutyPct} onChange={set("dutyPct")} testid="c13-duty" />
          <Field label="Clearance (₹)" value={v.clearance} onChange={set("clearance")} testid="c13-clear" />
        </div>
        <Field label="Local freight in market (₹/unit)" value={v.localFreight} onChange={set("localFreight")} testid="c13-local" />
        <div className="grid grid-cols-3 gap-4">
          <Field label="Importer margin %" value={v.importerMarginPct} onChange={set("importerMarginPct")} testid="c13-imp" />
          <Field label="Distributor margin %" value={v.distributorMarginPct} onChange={set("distributorMarginPct")} testid="c13-dist" />
          <Field label="Retailer margin %" value={v.retailerMarginPct} onChange={set("retailerMarginPct")} testid="c13-ret" />
        </div>
      </div>
      <div>
        <div data-testid="c13-result">
          <ResultRow label="CIF value" value={inr(r.cif)} />
          <ResultRow label="Duty" value={inr(r.duty)} />
          <ResultRow label="Landed cost" value={inr(r.landed)} strong />
          <ResultRow label="Required shelf price" value={inr(r.requiredShelfPrice)} strong tone={r.multiple > 2.5 ? "bad" : undefined} />
          <ResultRow label="Shelf price ÷ landed cost" value={`${r.multiple}×`} />
        </div>
        <Waterfall items={r.chain.map((c, i) => ({ label: c.label, value: c.value, total: i === r.chain.length - 1, display: inr2(c.value) }))} />
        <Note tone="warn">Margins apply on each partner's selling price, so they compound. Verify duty with the current customs tariff for your HS code — rates change with budgets and trade agreements.</Note>
      </div>
    </div>
  );
}

/* ---------- C14 Sell-In vs Sell-Through Analyzer ---------- */
const C14_DEFAULT = [
  { sku: "Core SKU 1", openingUnits: 1200, primaryIn: 2000, secondaryOut: 2400, weeks: 4.33 },
  { sku: "Core SKU 2", openingUnits: 900, primaryIn: 1500, secondaryOut: 1300, weeks: 4.33 },
  { sku: "Premium SKU", openingUnits: 700, primaryIn: 900, secondaryOut: 320, weeks: 4.33 },
  { sku: "New launch", openingUnits: 0, primaryIn: 1000, secondaryOut: 410, weeks: 4.33 },
];
export function ToolC14() {
  const [rows, setRows] = useState(C14_DEFAULT);
  const r = useMemo(() => sellThrough(rows), [rows]);
  const setR = (i, k) => (val) => setRows((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: val } : x)));
  const flagStyle = { overstock: "text-red-700", "stockout-risk": "text-amber-700", slow: "text-amber-700", healthy: "text-emerald-700" };
  return (
    <div data-testid="tool-C14">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="c14-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b border-black">
              {["SKU", "Opening stock", "Primary in (sell-in)", "Secondary out (sell-through)", "Closing", "Weeks of cover", "Sell-through %", "Flag"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const out = r.rows[i];
              return (
                <tr key={i} className="border-b border-black/10" data-testid={`c14-row-${i}`}>
                  <td className="py-2 pr-3"><input value={row.sku} onChange={(e) => setR(i, "sku")(e.target.value)} className="w-32 bg-transparent outline-none border-b border-transparent focus:border-black text-sm" /></td>
                  {["openingUnits", "primaryIn", "secondaryOut"].map((k) => (
                    <td key={k} className="py-2 pr-3"><input type="number" value={row[k]} onChange={(e) => setR(i, k)(e.target.value)} className="w-24 border border-zinc-200 px-1.5 py-1 text-sm outline-none focus:border-black" data-testid={`c14-${k}-${i}`} /></td>
                  ))}
                  <td className="py-2 pr-3 tabular-nums">{out.closingUnits.toLocaleString("en-IN")}</td>
                  <td className="py-2 pr-3 tabular-nums">{out.coverWeeks >= 99 ? "∞" : out.coverWeeks}</td>
                  <td className="py-2 pr-3 tabular-nums">{out.sellThroughPct}%</td>
                  <td className={`py-2 text-xs font-medium uppercase tracking-wide ${flagStyle[out.flag]}`} data-testid={`c14-flag-${i}`}>{out.flag}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={() => setRows((s) => [...s, { sku: `SKU ${s.length + 1}`, openingUnits: 0, primaryIn: 0, secondaryOut: 0, weeks: 4.33 }])} className="mt-4 text-sm font-medium underline underline-offset-4" data-testid="c14-add">+ Add SKU</button>
      <div className="mt-6 max-w-md" data-testid="c14-result">
        <ResultRow label="SKUs overstocked (≥8 weeks cover)" value={String(r.overstocked)} tone={r.overstocked ? "bad" : "good"} />
        <ResultRow label="SKUs at stockout risk (≤2 weeks)" value={String(r.atRisk)} tone={r.atRisk ? "bad" : "good"} strong />
      </div>
      <Note>Cover weeks = closing stock ÷ weekly secondary. Overstock today is next quarter's returns, expiry and discounting. If primary consistently exceeds secondary, the channel is being loaded — and the correction always comes.</Note>
    </div>
  );
}
